import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getChats } from "../../../api/api";
import { useInboxSubscription, useActiveInboxChat, useUserRealtime } from "../../../context/UserRealtimeContext";
import { ChatList } from "../ChatList";
import ChatFrame from "../chat_frame/ChatFrame";
import NewChatModal from "../NewChatModal";
import InboxSearchResults from "../InboxSearchResults";
import { useInboxSearch } from "../useInboxSearch";
import { isAccountVerified } from "../../utils";
import { MessageDockPill } from "./MessageDockPill";
import { MessageDockPanel } from "./MessageDockPanel";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { CyclingPlaceholderField } from "../../ui/cycling-placeholder-field";
import { INBOX_SEARCH_PLACEHOLDERS } from "../../../lib/searchPlaceholders";
import {
  bumpChatForNewMessage,
  clearChatUnread,
  getSharedMessagePreview,
  getTotalUnreadCount,
  shouldIncrementUnread,
  sortChatsByRecent,
  upsertChat,
  mergeChatByRecent,
} from "../chatListUtils";

/**
 * Floating messages entry point: pill when collapsed, panel with list or chat when open.
 * @param {{ userId: number, user?: object, userType?: string, userPfpUrl?: string }} props
 * @returns {JSX.Element | null}
 */
function MessageDock({ userId, user, userType, userPfpUrl }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pillRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [panelScreen, setPanelScreen] = useState(/** @type {'list' | 'chat'} */ ("list"));
  const [selectedChat, setSelectedChat] = useState(null);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [listQuery, setListQuery] = useState("");
  const [resultsDismissed, setResultsDismissed] = useState(false);
  const [focusMessageId, setFocusMessageId] = useState(null);
  const [focusKey, setFocusKey] = useState(0);

  const openChatIdRef = useRef(null);
  openChatIdRef.current =
    panelScreen === "chat" && selectedChat?.id != null
      ? Number(selectedChat.id)
      : null;
  const chatsRef = useRef(chats);
  chatsRef.current = chats;
  const { reportUnreadCleared } = useUserRealtime();

  const path = location.pathname;
  const isOrgChannels = /^\/organization\/[^/]+\/channels(?:\/|$)/.test(path);
  const isBeta = path === "/beta" || path.startsWith("/beta/");
  const isSignup =
    path === "/create-account" ||
    path === "/new-org" ||
    path === "/new-user" ||
    path === "/signup";
  const dockEnabled =
    Boolean(userId) &&
    isAccountVerified(user) &&
    userType !== "organization" &&
    !path.startsWith("/messaging") &&
    !isOrgChannels &&
    path !== "/" &&
    !isBeta &&
    !isSignup;

  useActiveInboxChat(
    panelScreen === "chat" ? selectedChat?.id : null,
    null,
    dockEnabled && panelScreen === "chat"
  );

  const showResults = listQuery.trim() !== "" && !resultsDismissed;
  const inboxSearch = useInboxSearch({
    userId,
    query: listQuery,
    enabled: dockEnabled && isOpen && showResults,
  });

  const totalUnread = useMemo(() => getTotalUnreadCount(chats), [chats]);

  const dismissPanel = useCallback(() => {
    setIsOpen(false);
    setPanelScreen("list");
    setSelectedChat(null);
    setShowNewChatModal(false);
    setListQuery("");
    setResultsDismissed(false);
    setFocusMessageId(null);
    setTimeout(() => pillRef.current?.focus(), 0);
  }, []);

  const expandToFullPage = useCallback(() => {
    if (panelScreen === "chat" && selectedChat?.id != null) {
      navigate(`/messaging/${selectedChat.id}`);
    } else {
      navigate("/messaging");
    }
    dismissPanel();
  }, [navigate, dismissPanel, panelScreen, selectedChat]);

  const refreshChats = useCallback(async () => {
    if (!userId) {
      setChats([]);
      return;
    }
    try {
      const res = await getChats(userId);
      if (res.success) {
        setChats(sortChatsByRecent(res.data || []));
      }
    } catch {
      // Keep existing list on transient failures.
    }
  }, [userId]);

  useEffect(() => {
    if (!dockEnabled) {
      setChats([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      await refreshChats();
      setLoading(false);
    })();
  }, [dockEnabled, refreshChats]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        dismissPanel();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, dismissPanel]);

  const updateChatLastMessage = useCallback((chatId, messageContent, messageTimestamp) => {
    const chatIdNum = parseInt(String(chatId), 10);
    setChats((prevChats) =>
      bumpChatForNewMessage(prevChats, chatIdNum, messageContent, messageTimestamp)
    );
    setSelectedChat((prev) => {
      if (prev && prev.id === chatIdNum) {
        const ts =
          messageTimestamp ||
          prev.last_message_timestamp ||
          prev.last_message_at ||
          "";
        return {
          ...prev,
          last_message_content: messageContent,
          last_message_timestamp: ts,
          last_message_at: ts || prev.last_message_at,
        };
      }
      return prev;
    });
  }, []);

  const handleInboxMessage = useCallback(
    (message) => {
      const chatIdNum = Number(message?.chat_id);
      if (!Number.isFinite(chatIdNum)) return;

      const content = getSharedMessagePreview(message);
      const ts = message.created_at || "";
      const increment = shouldIncrementUnread(
        message,
        userId,
        openChatIdRef.current
      );

      let missingChat = false;
      setChats((prev) => {
        const exists = (prev || []).some((c) => Number(c.id) === chatIdNum);
        if (!exists) {
          missingChat = true;
          return prev;
        }
        return bumpChatForNewMessage(prev, chatIdNum, content, ts, {
          incrementUnread: increment,
        });
      });
      if (missingChat) {
        refreshChats();
      }

      setSelectedChat((prev) => {
        if (prev && Number(prev.id) === chatIdNum) {
          return {
            ...prev,
            last_message_content: content,
            last_message_timestamp: ts || prev.last_message_timestamp,
            last_message_at: ts || prev.last_message_at,
          };
        }
        return prev;
      });
    },
    [userId, refreshChats]
  );

  const handleInboxChatRemoved = useCallback((removedChatId) => {
    setChats((prev) =>
      (prev || []).filter((c) => Number(c.id) !== Number(removedChatId))
    );
    setSelectedChat((prev) => {
      if (prev && Number(prev.id) === Number(removedChatId)) {
        setPanelScreen("list");
        return null;
      }
      return prev;
    });
  }, []);

  useInboxSubscription(
    {
      onInboxMessage: handleInboxMessage,
      onChatRemoved: handleInboxChatRemoved,
      onReconnect: refreshChats,
    },
    dockEnabled
  );

  const handleChatCreated = useCallback((newChat) => {
    setChats((prev) => upsertChat(prev, newChat));
    setSelectedChat(newChat);
    setPanelScreen("chat");
    setShowNewChatModal(false);
    setListQuery("");
    setResultsDismissed(false);
    setFocusMessageId(null);
  }, []);

  /**
   * Keep dock list + selection in sync when chat metadata changes.
   * @param {object} updated
   * @returns {void}
   */
  const handleChatUpdate = useCallback((updated) => {
    if (!updated?.id) return;
    setChats((prev) => mergeChatByRecent(prev, updated));
    setSelectedChat((prev) =>
      prev && Number(prev.id) === Number(updated.id) ? { ...prev, ...updated } : prev
    );
  }, []);

  /**
   * Close the open dock chat when the current user is removed.
   * @param {number} removedChatId
   * @returns {void}
   */
  const handleRemovedFromChat = useCallback((removedChatId) => {
    if (!removedChatId) return;
    setChats((prev) =>
      (prev || []).filter((c) => Number(c.id) !== Number(removedChatId))
    );
    setSelectedChat((prev) =>
      prev && Number(prev.id) === Number(removedChatId) ? null : prev
    );
    setPanelScreen("list");
  }, []);

  const handleMarkChatRead = useCallback(
    (id) => {
      const chat = (chatsRef.current || []).find(
        (c) => Number(c.id) === Number(id)
      );
      const cleared = Number(chat?.unread_count) || 0;
      if (cleared > 0) {
        reportUnreadCleared(cleared, null);
      }
      setChats((prev) => clearChatUnread(prev, id));
      setSelectedChat((prev) =>
        prev && Number(prev.id) === Number(id)
          ? { ...prev, unread_count: 0 }
          : prev
      );
    },
    [reportUnreadCleared]
  );

  const handleDockChatSelect = useCallback((chat) => {
    setResultsDismissed(true);
    setFocusMessageId(null);
    setSelectedChat(chat);
    setPanelScreen("chat");
  }, []);

  const handleSearchChat = useCallback((chat) => {
    setSelectedChat(chat);
    setFocusMessageId(null);
    setPanelScreen("chat");
    setResultsDismissed(true);
  }, []);

  const handleSearchMessage = useCallback(
    (hit) => {
      const existing = chats.find((c) => Number(c.id) === Number(hit.chat_id));
      const chat = existing || {
        id: hit.chat_id,
        name: hit.chat_name,
        chat_type: hit.chat_type,
      };
      setSelectedChat(chat);
      setFocusMessageId(Number(hit.id));
      setFocusKey((k) => k + 1);
      setPanelScreen("chat");
      setResultsDismissed(true);
    },
    [chats]
  );

  const handleSearchInput = useCallback((value) => {
    setListQuery(value);
    setResultsDismissed(false);
  }, []);

  const togglePill = useCallback(() => {
    setIsOpen((open) => {
      const next = !open;
      if (!next) {
        setTimeout(() => pillRef.current?.focus(), 0);
      }
      return next;
    });
  }, []);

  if (!dockEnabled) {
    return null;
  }

  const listHeader = (
    <div className="flex items-center justify-between bg-secondary px-4 py-3">
      <h2 className="text-lg font-semibold text-text-primary">Messages</h2>
      <div className="flex items-center gap-0.5">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-text-muted hover:text-text-secondary"
          onClick={expandToFullPage}
          title="Open full messaging"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-text-muted hover:text-text-secondary"
          onClick={dismissPanel}
          title="Close"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </Button>
      </div>
    </div>
  );

  const panelActions = {
    onBack: () => {
      setPanelScreen("list");
      setSelectedChat(null);
      setFocusMessageId(null);
    },
    onExpand: expandToFullPage,
    onClose: dismissPanel,
  };

  const searchField = (
    <div className="shrink-0 border-b border-ui-border px-3 py-2">
      <CyclingPlaceholderField
        placeholders={INBOX_SEARCH_PLACEHOLDERS}
        isEmpty={listQuery.trim() === ""}
        className="w-full"
        overlayClassName="px-3 text-base md:text-sm"
      >
        <Input
          type="search"
          value={listQuery}
          onChange={(e) => handleSearchInput(e.target.value)}
          placeholder=""
          aria-label="Search chats and messages"
          className="h-9 bg-ui-card placeholder:text-transparent"
        />
      </CyclingPlaceholderField>
    </div>
  );

  return (
    <div className="fixed bottom-4 right-4 z-50 hidden flex-col items-end gap-3 md:flex">
      {isOpen && (
        <MessageDockPanel
          header={showResults || panelScreen === "list" ? listHeader : null}
        >
          {showResults ? (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {searchField}
              <InboxSearchResults
                userId={userId}
                query={listQuery}
                chats={inboxSearch.chats}
                messages={inboxSearch.messages}
                loading={inboxSearch.loading}
                error={inboxSearch.error}
                onSelectChat={handleSearchChat}
                onSelectMessage={handleSearchMessage}
                onChatRead={handleMarkChatRead}
              />
            </div>
          ) : panelScreen === "list" ? (
            <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
              {searchField}
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <ChatList
                  userId={userId}
                  chats={chats}
                  loading={loading}
                  selectedChat={selectedChat}
                  onChatSelect={handleDockChatSelect}
                  onChatRead={handleMarkChatRead}
                  emptyLabel="No chats found."
                />
              </div>
              <Button
                type="button"
                size="icon"
                className="absolute bottom-3 right-3 h-12 w-12 rounded-full shadow-md"
                onClick={() => setShowNewChatModal(true)}
                title="New message"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </Button>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {searchField}
              {selectedChat ? (
                <ChatFrame
                  key={selectedChat.id}
                  userId={userId}
                  user={user}
                  chat={selectedChat}
                  chatId={selectedChat.id}
                  onMessageUpdate={updateChatLastMessage}
                  panelActions={panelActions}
                  compactComposer
                  currentUserPfp={userPfpUrl}
                  onChatUpdate={handleChatUpdate}
                  onRemovedFromChat={handleRemovedFromChat}
                  focusMessageId={focusMessageId}
                  focusKey={focusKey}
                />
              ) : null}
            </div>
          )}
        </MessageDockPanel>
      )}

      {!isOpen && (
        <MessageDockPill
          buttonRef={pillRef}
          onClick={togglePill}
          previewChats={chats}
          unreadCount={totalUnread}
        />
      )}

      {showNewChatModal && (
        <NewChatModal
          userId={userId}
          onClose={() => setShowNewChatModal(false)}
          onChatCreated={handleChatCreated}
        />
      )}
    </div>
  );
}

export default MessageDock;
